"use client";

import {
  Building2,
  Crown,
  RefreshCw,
  Settings,
  ShieldCheck,
  User,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { handleError } from "@/shared/api/error-handler";
import {
  testerApi,
  type TesterRoleSwitch,
  type TesterStatus,
} from "@/shared/api/tester";
import { Button } from "@/shared/ui/buttons/button";
import { Badge } from "@/shared/ui/data-display/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";
import { Separator } from "@/shared/ui/layout/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/modals/dialog";

export function TesterPanel() {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const [isOpen, setIsOpen] = useState(false);
  const [testerStatus, setTesterStatus] = useState<TesterStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState<
    { id: string; name: string }[]
  >([]);

  // Состояние для смены роли
  const [roleSwitch, setRoleSwitch] = useState<TesterRoleSwitch>({
    role: "participant",
  });

  useEffect(() => {
    const checkTesterStatus = async () => {
      try {
        const status = await testerApi.getStatus();
        setTesterStatus(status);

        if (status.isTester) {
          // Загружаем организации для выбора
          // KAN-975: API организаций (zod + схемы) — только тестеру; статический import тянул его
          // в общую оболочку лейаута всем посетителям.
          const { organizationsApi } = await import("@/shared/api/organizations");
          const orgs = await organizationsApi.getAllOrganizations();
          setOrganizations(orgs);
        }
      } catch (_error) {
        setTesterStatus({ isTester: false });
      } finally {
        setIsLoading(false);
      }
    };

    // Проверяем только в dev режиме
    if (process.env.NODE_ENV === "development") {
      checkTesterStatus();
    } else {
      setIsLoading(false);
    }
  }, []);

  // Не показываем в продакшене или если не тестер
  if (
    process.env.NODE_ENV !== "development" ||
    isLoading ||
    !testerStatus?.isTester
  ) {
    return null;
  }

  const handleRoleSwitch = async () => {
    setIsSubmitting(true);

    try {
      const result = await testerApi.switchRole(roleSwitch);

      if (result.success) {
        toast.success(tToast("testerPanel.roleChanged", { role: roleSwitch.role }));

        // Обновляем статус
        const updatedStatus = await testerApi.getStatus();
        setTesterStatus(updatedStatus);

        // Перезагружаем страницу для применения изменений
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        handleError(new Error("Ошибка при смене роли"), {
          component: "TesterPanel",
          action: "switchRole",
        });
      }
    } catch (error) {
      handleError(error, { component: "TesterPanel", action: "switchRole" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role.toLowerCase()) {
      case "admin":
        return <ShieldCheck className="h-4 w-4" />;
      case "captain":
        return <Crown className="h-4 w-4" />;
      case "member":
        return <Users className="h-4 w-4" />;
      case "orgadmin":
        return <Building2 className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case "admin":
        return "bg-red-500";
      case "captain":
        return "bg-yellow-500";
      case "member":
        return "bg-blue-500";
      case "orgadmin":
        return "bg-purple-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <>
      <div className="fixed top-4 right-20 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          variant="outline"
          size="sm"
          className="bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-600"
        >
          <Settings className="mr-2 h-4 w-4" />
          {tUi("testerPanel.tester")}
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-yellow-500" />
              {tUi("testerPanel.panelTestirovschika")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Текущий статус */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium">{tUi("testerPanel.tekuschiyStatus")}</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {tUi("testerPanel.polzovatel")}
                  </span>
                  <Badge variant="outline">{testerStatus?.displayName}</Badge>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{tUi("testerPanel.roli")}</span>
                  <div className="flex gap-1">
                    {testerStatus?.roles?.map((role, index) => (
                      <Badge key={index} className={getRoleColor(role)}>
                        {getRoleIcon(role)}
                        <span className="ml-1">{role}</span>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Команды */}
                {testerStatus?.teamMemberships &&
                  testerStatus.teamMemberships.length > 0 && (
                    <div>
                      <span className="text-sm text-muted-foreground">
                        {tUi("testerPanel.komandy")}
                      </span>
                      <div className="mt-1 space-y-1">
                        {testerStatus.teamMemberships.map((team, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between text-xs"
                          >
                            <span>{team.teamName}</span>
                            <Badge variant="outline" className="text-xs">
                              {team.role}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Организации */}
                {testerStatus?.organizationMemberships &&
                  testerStatus.organizationMemberships.length > 0 && (
                    <div>
                      <span className="text-sm text-muted-foreground">
                        {tUi("testerPanel.organizatsii")}
                      </span>
                      <div className="mt-1 space-y-1">
                        {testerStatus.organizationMemberships.map(
                          (org, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between text-xs"
                            >
                              <span>{org.organizationName}</span>
                              <Badge variant="outline" className="text-xs">
                                Role {org.roleId}
                              </Badge>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            <Separator />

            {/* Смена роли */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium">{tUi("testerPanel.smenitRol")}</h3>

              <div className="space-y-3">
                <div>
                  <label htmlFor="tester-role-switch" className="text-xs text-muted-foreground">{tUi("testerPanel.rol")}</label>
                  <Select
                    value={roleSwitch.role}
                    onValueChange={(value) =>
                      setRoleSwitch((prev) => ({ ...prev, role: value }))
                    }
                  >
                    <SelectTrigger id="tester-role-switch" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4" />
                          {tUi("testerPanel.adminSayta")}
                        </div>
                      </SelectItem>
                      <SelectItem value="participant">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          {tUi("testerPanel.uchastnik")}
                        </div>
                      </SelectItem>
                      <SelectItem value="captain">
                        <div className="flex items-center gap-2">
                          <Crown className="h-4 w-4" />
                          {tUi("testerPanel.kapitanKomandy")}
                        </div>
                      </SelectItem>
                      <SelectItem value="member">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {tUi("testerPanel.chlenKomandy")}
                        </div>
                      </SelectItem>
                      <SelectItem value="orgadmin">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {tUi("testerPanel.adminOrganizatsii")}
                        </div>
                      </SelectItem>
                      <SelectItem value="orgmember">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {tUi("testerPanel.chlenOrganizatsii")}
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Выбор организации для org ролей */}
                {(roleSwitch.role === "orgadmin" ||
                  roleSwitch.role === "orgmember") && (
                  <div>
                    <label htmlFor="tester-org-switch" className="text-xs text-muted-foreground">
                      {tUi("testerPanel.organizatsiya")}
                    </label>
                    <Select
                      value={roleSwitch.organizationId || ""}
                      onValueChange={(value) =>
                        setRoleSwitch((prev) => ({
                          ...prev,
                          organizationId: value,
                        }))
                      }
                    >
                      <SelectTrigger id="tester-org-switch" className="w-full">
                        <SelectValue placeholder={tUi("testerPanel.vyberiteOrganizatsiyu")} />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  onClick={handleRoleSwitch}
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      {tUi("testerPanel.primenenie")}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {tUi("testerPanel.primenitRol")}
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="text-xs text-muted-foreground text-center">
              {tUi("testerPanel.izmeneniyaPrimenyatsyaPoslePerezagruzki")}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
